/**
 * BulkBar — sticky bulk-action toolbar for the products table (STORY-008-007,
 * AC#7). Appears when ≥1 product is selected. Two actions are gated by the
 * caller on `marketplace.bulk_import` (the BulkBar is only rendered when the
 * caller holds it):
 *   - Set stock  → POST /admin/marketplace/products:bulk-stock-state
 *   - Delete     → POST /admin/marketplace/products:bulk-soft-delete (the caller
 *                  routes this through a confirm dialog — destructive).
 *
 * The "Set attribute…" action is gated SEPARATELY on `marketplace.product.edit`
 * (`canSetAttribute` — PIM Wave P5 Task 8 review fix): the backend
 * `products:bulk-set-attribute` route requires `product.edit`, not
 * `bulk_import`, so the button only renders when the caller passes that
 * permission too — otherwise a `bulk_import`-only admin would see a button
 * that 403s. (Known follow-up, not fixed here: a `product.edit`-only admin
 * without `bulk_import` still can't reach the BulkBar at all, since the page
 * only renders it under `marketplace.bulk_import`.)
 *
 * Presentational: state (selection, pending) lives in the page.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { PRODUCT_STOCK_STATES } from "@/features/admin/products/api";

export interface BulkBarProps {
  count: number;
  isPending: boolean;
  onApplyStock: (stockState: string) => void;
  onDeleteClick: () => void;
  onClear: () => void;
  /** Opens the "Set attribute…" dialog (PIM Wave P5 Task 8). */
  onSetAttributeClick: () => void;
  /** Whether to render the "Set attribute…" button. The caller passes
   * `useRequirePermission("marketplace.product.edit")` — that route needs
   * `product.edit`, not `bulk_import` (review fix), so the button must be
   * gated separately from the rest of the bar. */
  canSetAttribute: boolean;
}

const SELECT_CLASS =
  "min-h-[44px] rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

export function BulkBar({
  count,
  isPending,
  onApplyStock,
  onDeleteClick,
  onClear,
  onSetAttributeClick,
  canSetAttribute,
}: BulkBarProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const [stockState, setStockState] = useState<string>(PRODUCT_STOCK_STATES[0]);

  return (
    <div
      data-testid="admin-products-bulk-bar"
      className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-[var(--g-hairline)] bg-[var(--g-surface)] px-4 py-3 shadow-[var(--g-shadow-md)]"
    >
      <span data-testid="admin-products-bulk-count" className="text-sm font-medium">
        {t("products.bulk.selected", { count })}
      </span>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="bulk-stock-select">
          {t("products.bulk.setStock")}
        </label>
        <select
          id="bulk-stock-select"
          data-testid="admin-products-bulk-stock-select"
          value={stockState}
          onChange={(e) => setStockState(e.target.value)}
          className={SELECT_CLASS}
        >
          {PRODUCT_STOCK_STATES.map((s) => (
            <option key={s} value={s}>
              {t(`products.stock.${s}`)}
            </option>
          ))}
        </select>
        <Button
          variant="secondary"
          size="sm"
          data-testid="admin-products-bulk-apply-stock"
          loading={isPending}
          onClick={() => onApplyStock(stockState)}
          className="min-h-[44px]"
        >
          {t("products.bulk.apply")}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          data-testid="admin-products-bulk-delete"
          disabled={isPending}
          onClick={onDeleteClick}
          className="min-h-[44px]"
        >
          {t("products.bulk.delete")}
        </Button>
        {canSetAttribute ? (
          <Button
            variant="secondary"
            size="sm"
            data-testid="admin-products-bulk-set-attribute"
            disabled={isPending}
            onClick={onSetAttributeClick}
            className="min-h-[44px]"
          >
            {t("products.bulk.setAttribute")}
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="sm"
          data-testid="admin-products-bulk-clear"
          disabled={isPending}
          onClick={onClear}
          className="min-h-[44px]"
        >
          {t("products.bulk.clear")}
        </Button>
      </div>
    </div>
  );
}

export default BulkBar;
